import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  InAppCampaignActionType,
  InAppCampaignCloseEvent,
  InAppCampaignCloseReason,
  InAppCampaignMediaType,
  InAppCampaignModal,
} from 'src/app/interface/in-app-campaign.interface';
import { InAppCampaignService } from 'src/app/services/in-app-campaign.service';
import { CssOverflow } from 'src/app/shared/constants/constants';
import { toVideoEmbedUrl } from './video-embed.util';

let uniqueId = 0;

/**
 * RN-5 admin-facing modal. Reused as-is (with `[preview]="true"`) by the super-admin console's
 * live preview (PR4b) — no duplicate template, per design.
 */
@Component({
  selector: 'app-in-app-campaign-modal',
  templateUrl: './in-app-campaign-modal.component.html',
  styleUrls: ['./in-app-campaign-modal.component.scss'],
})
export class InAppCampaignModalComponent implements AfterViewInit, OnDestroy {
  @Input() campaign: InAppCampaignModal;
  /** Disables the fixed scrim, the focus trap, and all HTTP calls — the console preview reuses this component. */
  @Input() preview = false;
  @Output() closed = new EventEmitter<InAppCampaignCloseEvent>();

  @ViewChild('modalEl') private readonly _modalElRef?: ElementRef<HTMLElement>;
  @ViewChild('primaryButton') private readonly _primaryButtonRef?: ElementRef<HTMLButtonElement>;

  readonly titleId = `in-app-campaign-modal-title-${(uniqueId += 1)}`;

  /**
   * A media block only makes sense once there is something to show. Testing `type !== 'none'`
   * alone rendered an `<img src="">` while the console preview was still empty or mid-upload, and
   * threw outright when `media` itself was undefined — which is what made the preview go blank
   * instead of picking the image up after an upload.
   */
  get hasMedia(): boolean {
    return !!this.campaign?.media?.url && this.campaign.media.type !== InAppCampaignMediaType.none;
  }

  get isImage(): boolean {
    return this.hasMedia && this.campaign.media.type === InAppCampaignMediaType.image;
  }

  get isVideo(): boolean {
    return this.hasMedia && this.campaign.media.type === InAppCampaignMediaType.video;
  }

  /** A YouTube/Vimeo share link — `<video>` cannot play those, so the template renders an `<iframe>`. */
  get isEmbeddedVideo(): boolean {
    return this.isVideo && !!this.videoEmbedUrl;
  }

  /** A direct media file (`.mp4`, `.webm`, an S3 object…) — still the `<video>` element's job. */
  get isFileVideo(): boolean {
    return this.isVideo && !this.videoEmbedUrl;
  }

  /**
   * Memoised on the raw url: an `[src]` binding that handed the iframe a **new** `SafeResourceUrl`
   * object on every change-detection pass would reload the embed on every tick.
   */
  get videoEmbedUrl(): SafeResourceUrl | null {
    const raw = this.campaign?.media?.url ?? null;
    if (raw !== this._embedSourceUrl) {
      this._embedSourceUrl = raw;
      const embed = toVideoEmbedUrl(raw);
      // The url is provider-validated by `toVideoEmbedUrl` (https + known host) before it is trusted.
      this._embedSafeUrl = embed ? this._sanitizer.bypassSecurityTrustResourceUrl(embed) : null;
    }
    return this._embedSafeUrl;
  }

  private _focusTrap?: FocusTrap;
  private _previouslyFocusedElement: HTMLElement | null = null;
  private _embedSourceUrl: string | null | undefined;
  private _embedSafeUrl: SafeResourceUrl | null = null;

  constructor(
    private readonly _service: InAppCampaignService,
    private readonly _focusTrapFactory: FocusTrapFactory,
    private readonly _sanitizer: DomSanitizer,
  ) {}

  ngAfterViewInit(): void {
    if (this.preview) return;

    this._previouslyFocusedElement = document.activeElement as HTMLElement;
    document.body.style.overflow = CssOverflow.hidden;

    if (this._modalElRef) {
      this._focusTrap = this._focusTrapFactory.create(this._modalElRef.nativeElement);
    }
    // Initial focus lands on the primary button regardless of what the focus trap's own
    // heuristic would pick first (e.g. the close button).
    this._primaryButtonRef?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this._focusTrap?.destroy();
    if (!this.preview) {
      document.body.style.overflow = CssOverflow.unset;
      this._previouslyFocusedElement?.focus();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKeydown(): void {
    if (this.preview) return;
    this.onDismiss();
  }

  onScrimClick(event: MouseEvent): void {
    if (this.preview) return;
    // Only the scrim itself (not a bubbled click from inside the modal) dismisses.
    if (event.target === event.currentTarget) {
      this.onDismiss();
    }
  }

  onPrimaryClick(): void {
    if (!this.preview) {
      // Fire-and-forget (MOD-5): the modal closes immediately regardless of the POST's outcome.
      // The service already swallows HTTP failures; the no-op error handler here is a second,
      // defensive layer so an unhandled-error throw can never block the `closed.emit` below.
      this._service.registerClick(this.campaign.id).subscribe({ error: () => undefined });
      if (this.campaign.action?.type === InAppCampaignActionType.link && this.campaign.action.url) {
        window.open(this.campaign.action.url, '_blank', 'noopener');
      }
    }
    this.closed.emit({
      reason: InAppCampaignCloseReason.click,
      actionType: this.campaign?.action?.type,
    });
  }

  onDismiss(): void {
    if (!this.preview) {
      // Fire-and-forget (MOD-5): a failed dismiss report never reopens the modal.
      this._service.registerDismiss(this.campaign.id).subscribe({ error: () => undefined });
    }
    this.closed.emit({ reason: InAppCampaignCloseReason.dismiss });
  }
}
